import React, { FormEvent, useEffect, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, Checkbox, FormControlLabel, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { useConfirm } from 'material-ui-confirm';

import { ICustomerDetails } from '../../types/customer';
import { IFuzzySearchResult } from '../../types/system';

import { AppConfig } from '../../config/appConfig';
import { useListContext } from '../../contexts/listContext';
import { useUserContext } from '../../contexts/userContext';

import { ErrorProvider } from '../../classes/errorProvider';
import { CustomerService } from '../../services/customerService';
import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import Spinner from '../spinner';

import '../../styles/customerDetailsForm.scss';
import Json from '../json';

export interface ICustomerDetailsForm {
	action: "add" | "edit";
	id?: string;
	details: ICustomerDetails;
	brandsRequiringApiKeys: string[];
	reloadData: () => void;
}

const CustomerDetailsForm: React.FunctionComponent<ICustomerDetailsForm> = ({ action, id, details, brandsRequiringApiKeys, reloadData }) => {
	const confirm = useConfirm();
	const listContext = useListContext();
	const userContext = useUserContext();
	const navigate = useNavigate();
	const [formFields, setFormFields] = useState<ICustomerDetails>(details);
	const [originalCompanyName, setOriginalCompanyName] = useState<string>(details?.name);
	const [isSaving, setIsSaving] = useState(false);
	const [isCheckingNameForDupes, setIsCheckingNameForDupes] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [successMessage, setSuccessMessage] = useState<string | undefined>();
	const [isApiKeyRequired, setIsApiKeyRequired] = useState<boolean | undefined>(undefined);

	useEffect(() => {
		if (Array.isArray(brandsRequiringApiKeys) && formFields.brandId && Array.isArray(listContext.brands)) {
			const brandIds = brandsRequiringApiKeys.map(b => listContext.brands.find(lb => lb.name == b)?.value).filter(b => b != undefined);
			setIsApiKeyRequired(brandIds.includes(formFields.brandId.toString()));
		}
		else {
			setIsApiKeyRequired(undefined);
		}
	}, [brandsRequiringApiKeys, formFields.brandId, listContext.brands]);

	const getBrandName = (brandId: string) => {
		return listContext.brands.find(b => b.value == brandId)?.name;
	}

	const doSave = async () => {
		clearSaveResult();
		setIsSaving(true);
		const payload: ICustomerDetails = { ...formFields, brandId: formFields.brandId || null };
		if (!isApiKeyRequired) payload.apiKeyOptOut = false; // Clear the opt-out so it's not enabled if there's no API key requirement

		if (action == "add") {
			CustomerService.addCustomer(payload)
				.then((response) => {
					console.log('CustomerService.addCustomer: SUCCESS', response);
					setSuccessMessage("Customer has been added.");
					setTimeout(() => {
						navigate("/customer/" + response.data.id);
					}, AppConfig.addSuccessToastMs);
				})
				.catch((error) => {
					console.log('CustomerService.addCustomer: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsSaving(false);
					window.scroll(0, 0);
				});
		}
		else if (action == "edit" && id) {
			CustomerService.setDetails(id, payload)
				.then((response) => {
					console.log('CustomerService.setDetails: SUCCESS', response);
					reloadData();
					setSuccessMessage("Customer details have been saved.");
					setTimeout(() => {
						setSuccessMessage(undefined);
					}, AppConfig.editSuccessToastMs);
				})
				.catch((error) => {
					console.log('CustomerService.setDetails: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsSaving(false);
					window.scrollTo(0, 0);
				});
		}
	}

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		// Fuzzy check existing customer names for potential duplicates before saving		
		event.preventDefault();
		setErrorMessage(undefined);

		// If the name wasn't changed then don't bother checking for duplicates
		if (action == "edit" && formFields?.name == originalCompanyName) {
			await doSave();
			return;
		}

		setIsCheckingNameForDupes(true);

		CustomerService.fuzzySearchForDuplicateCompanyNames({ name: formFields?.name, excludeId: action == "edit" ? +id : undefined })
			.then(async (response) => {
				console.log('CustomerService.fuzzySearchForDuplicateCompanyNames: SUCCESS', response);
				const matches: IFuzzySearchResult[] = response.data;
				if (matches.length > 0) {
					if (await UtilityService.ConfirmationModal(
						confirm,
						<div className="dupes-dialog-title">
							<Icons.Warning fill="#272f50" height="14px"></Icons.Warning>
							Potential Duplicates
						</div>,
						<div className="dupes-dialog-content">
							The name <strong>{formFields.name}</strong> is similar to <strong>{matches.length}</strong> existing customer{matches.length > 1 ? "s" : ""}. Please review the conflicts below before proceeding.<br />
							<ul>
								{matches.map((item, index) => (
									<li key={index}>
										<a href={`/customer/${item.id}`} target="_blank" rel="noopener noreferrer">
											<Icons.ViewItem height="14px"></Icons.ViewItem>
											{item.name}
										</a>
									</li>
								))}
							</ul>
						</div>,
						{
							confirmationText: "Continue",
							confirmationButtonProps: { color: 'primary', variant: 'contained' },
							cancellationText: "Cancel"
						}
					) == false) {
						return;
					}
				}

				await doSave();
			})
			.catch((error) => {
				console.log('CustomerService.fuzzySearchForDuplicateCompanyNames: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsCheckingNameForDupes(false);
			});
	}

	const clearSaveResult = () => {
		setErrorMessage(undefined);
		setSuccessMessage(undefined);
	};

	const enableClosedThroughDate = (enabled: boolean) => {
		setFormFields({ ...formFields, ["closedThroughDate"]: enabled ? dayjs().toDate() : undefined });
	}

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<section className="hwi-customer-details-form">
				{successMessage &&
					<Alert className="alert-success"
						severity="success"
						action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
						{successMessage}
					</Alert>
				}
				{errorMessage &&
					<Alert className="alert-error"
						severity="error"
						action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
						{errorMessage}
					</Alert>
				}
				<Form onSubmit={(event) => handleSubmit(event)}>
					<div className="row">
						<InputLabel>Company Name *</InputLabel>
						<TextField className="name-field" required name="name" value={formFields.name} size="small" onChange={(event) => { setFormFields({ ...formFields, ["name"]: event.target.value }) }} inputProps={{ maxLength: 100 }}></TextField>
					</div>
					<div className="row">
						<InputLabel>Brand</InputLabel>
						<Select
							value={formFields.brandId}
							className="brand-field"
							onChange={(event) => { setFormFields({ ...formFields, ["brandId"]: event.target.value, ["brandName"]: getBrandName(event.target.value) ?? null }) }}
						>
							<MenuItem value="" key={0}>None</MenuItem>
							{listContext.brands.map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>Status *</InputLabel>
						<Select
							value={formFields.statusId}
							className="status-field"
							required
							onChange={(event) => { setFormFields({ ...formFields, ["statusId"]: event.target.value.toString() }) }}
						>
							{listContext.customerStatuses.map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>EPA ID *</InputLabel>
						<TextField className="epa-id-field" required name="epaId" value={formFields.epaId} size="small" onChange={(event) => { setFormFields({ ...formFields, ["epaId"]: event.target.value }) }} inputProps={{ maxLength: 12 }}></TextField>
					</div>
					<div className="row">
						<InputLabel>Generator Status *</InputLabel>
						<Select
							value={formFields.generatorStatusId}
							className="generator-status-field"
							required
							onChange={(event) => { setFormFields({ ...formFields, ["generatorStatusId"]: event.target.value }) }}
						>
							{listContext.customerGeneratorStatuses.map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<FormControlLabel className="waste-certification-field" control={<Checkbox name="wasteCertification" value={formFields.wasteCertification} checked={formFields.wasteCertification} size="small" onChange={(event) => { setFormFields({ ...formFields, ["wasteCertification"]: event.target.checked }) }}></Checkbox>} label="Waste Certification" />
					</div>
					<div className="row">
						<InputLabel>Closed-Through Date</InputLabel>
						<div className="closed-through-date-field">
							{userContext?.permissions?.canChangeClosedThroughDate ? (
								<DatePicker className="start-date-field" value={dayjs(formFields?.closedThroughDate)} format='MM/DD/YYYY' onChange={(value) => setFormFields({ ...formFields, ["closedThroughDate"]: value?.startOf('day').toDate() })} />
							) : (
								<span>On or before {formFields?.closedThroughDate ? dayjs(formFields.closedThroughDate).format('MMMM D, YYYY') : 'N/A'}</span>
							)}
						</div>
					</div>
					<Spinner className="api-key-spinner" dependency={formFields?.brandId && isApiKeyRequired == undefined} text="Checking API Key requirements...">
						{!(formFields?.brandId && isApiKeyRequired && formFields?.apiKeyOptOut) &&
							<div className="row">
								<InputLabel>API Key {isApiKeyRequired ? "*" : ""}</InputLabel>
								<TextField
									className="api-key-field"
									name="apiKey"
									value={formFields.apiKey}
									size="small"
									required={isApiKeyRequired}
									placeholder={isApiKeyRequired ? `Required by ${formFields.brandName ?? "brand"}` : "Optional"}
									onChange={(event) => { setFormFields({ ...formFields, ["apiKey"]: event.target.value }) }}
									inputProps={{ maxLength: 100 }}
								></TextField>
							</div>
						}
						{isApiKeyRequired &&
							<div className="row">
								<FormControlLabel className="api-key-opt-out-field" control={<Checkbox name="apiKeyOptOut" value={formFields.apiKeyOptOut} checked={formFields.apiKeyOptOut} size="small" onChange={(event) => { setFormFields({ ...formFields, ["apiKeyOptOut"]: event.target.checked }) }}></Checkbox>} label="This customer does not use an API" />
							</div>
						}
					</Spinner>
					<div className="row">
						<InputLabel>Notes</InputLabel>
						<TextField className="notes-field" name="notes" multiline={true} rows={3} value={formFields.notes} size="small" onChange={(event) => { setFormFields({ ...formFields, ["notes"]: event.target.value }) }}></TextField>
					</div>
					<div className="row controls">
						<Button type="submit" className="save-button" disabled={isCheckingNameForDupes || isSaving} variant="contained">{isCheckingNameForDupes || isSaving ? "Saving..." : "Save Details"}</Button>
					</div>
				</Form>
				{/* <Json data={formFields}></Json> */}
			</section>
		</LocalizationProvider>
	)
}

export default CustomerDetailsForm;